"use client";

import { useState } from "react";
import { Tag } from "lucide-react";

interface HeroProps {
    onPurchase: (customCoupon?: string) => void;
    isLoading: boolean;
    couponCode: string;
    setCouponCode: (code: string) => void;
}

const Hero: React.FC<HeroProps> = ({ onPurchase, isLoading, couponCode, setCouponCode }) => {
    const [showCouponInput, setShowCouponInput] = useState(!!couponCode);

    const handleApplyCoupon = () => {
        onPurchase(couponCode);
    };

    return (
        <section className="bg-black text-white pt-16 pb-12 px-4">
            <div className="max-w-4xl mx-auto text-center">

                {/* Badge minimalista */}
                <div className="inline-block bg-cyan-400/10 border border-cyan-400/30 rounded-full px-4 py-1 mb-6">
                    <span className="text-cyan-400 text-xs font-bold uppercase tracking-widest">
                        Launch Offer · $9.99 · Full Code Included
                    </span>
                </div>

                {/* Título móvil */}
                <h1 className="sm:hidden text-4xl font-extrabold mb-6 leading-tight tracking-tight">
                    Build a Full Referral System
                    <span className="text-cyan-400"> with Next.js, Prisma & Stripe</span>
                </h1>

                {/* Título desktop */}
                <h1 className="hidden sm:block text-4xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
                    Build a Full Referral System <br />
                    <span className="text-cyan-400">with Next.js, Prisma & Stripe</span>
                </h1>

                {/* Descripción */}
                <div className="max-w-2xl mx-auto mb-10">
                    <p className="text-lg text-gray-200 leading-snug mb-8 text-center">
                        <strong>Complete tutorial & source code.</strong> Learn how to build a production-ready affiliate system with commission tracking, Stripe Connect payouts, and admin dashboard. No fluff—just working code you can deploy today.
                    </p>

                    {/* Screenshot horizontal */}
                    <div className="relative rounded-lg overflow-hidden border-2 border-cyan-400/30 shadow-[0_0_30px_rgba(34,211,238,0.2)]">
                        <img
                            src="/referrals-tutorial/referrals.png"
                            alt="Referrals System Dashboard Screenshot"
                            className="w-full h-auto"
                        />
                    </div>
                </div>

                {/* CTA Principal */}
                <div className="flex flex-col items-center">
                    <button
                        onClick={() => onPurchase()}
                        disabled={isLoading}
                        className={`bg-cyan-400 hover:bg-cyan-300 text-black font-bold py-4 px-12 rounded-full text-xl transition-all hover:scale-105 shadow-lg shadow-cyan-400/20 ${isLoading ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                    >
                        {isLoading ? "Processing..." : "Get the Code — $9.99"}
                    </button>

                    {/* Coupon Input Toggle */}
                    <div className="mt-4 w-full max-w-md">
                        {!showCouponInput ? (
                            <button
                                onClick={() => setShowCouponInput(true)}
                                className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center justify-center gap-2 mx-auto transition-colors"
                            >
                                <Tag size={16} />
                                Have a promo code?
                            </button>
                        ) : (
                            <div className="flex gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="PROMO CODE"
                                    disabled={isLoading}
                                    className="flex-1 px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors uppercase"
                                />
                                <button
                                    onClick={handleApplyCoupon}
                                    disabled={isLoading || !couponCode}
                                    className={`bg-cyan-400 hover:bg-cyan-300 text-black font-semibold px-6 py-2 rounded-lg transition-all ${
                                        isLoading || !couponCode ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                >
                                    Apply
                                </button>
                            </div>
                        )}
                    </div>

                    <p className="text-sm text-gray-400 mt-4">
                        Instant Access · Full Source Code · No Refunds
                    </p>
                </div>

                {/* Lo que obtienen */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-left border-t border-zinc-800 pt-10 text-lg">
                    <div>
                        <h3 className="text-cyan-400 font-bold mb-1">Full System</h3>
                        <p className="text-md text-gray-400">Referral tracking, commission calculation, and payouts.</p>
                    </div>
                    <div>
                        <h3 className="text-cyan-400 font-bold mb-1">Stripe Connect</h3>
                        <p className="text-md text-gray-400">Automatic affiliate payments with Stripe Connect integration.</p>
                    </div>
                    <div>
                        <h3 className="text-cyan-400 font-bold mb-1">Admin Dashboard</h3>
                        <p className="text-md text-gray-400">Manage affiliates, track commissions, process payouts.</p>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Hero;
